import axios from 'axios'
import { apiClient } from './client'
import { getConfigurationDrifts } from './drift'
import { mapDriftTrendSeries } from '../derive-trend-stats'
import { readActiveDrifts } from '../known-counts'
import type {
  ConfigurationDrift,
  ComplianceFrameworkImpact,
  DashboardStats,
  DashboardTrends,
  MetricTrendResult,
  ApiResponse,
} from '@/types'

/**
 * Dashboard + Command Center data: the tenant score card, drift trend
 * sparkline, activity feed, and the legacy baseline/framework reads. Split
 * out of `lib/api.ts` (WO-10.4) — see `lib/api.ts` for the barrel that
 * re-assembles this alongside the other domain modules into the `api`
 * object every caller already imports.
 */

/**
 * Activity feed row from `GET /activity/tenant/{id}` (`ActivityController` /
 * `ActivityItem` in Core.Models). The Container's JSON options are
 * camelCase-policy (`Program.cs`), so the wire shape matches this directly.
 */
export interface ActivityFeedItem {
  id: string
  type: string
  title: string
  description: string
  timestamp: string
  severity?: string | null
  tenantName?: string | null
  driftId?: string | null
  actionBy?: string | null
}

/** One aggregated point in the 30-day drift trend chart (`getDriftTrends`). */
export interface DriftTrendPoint {
  date: string
  critical: number
  high: number
  medium: number
  low: number
  info: number
}

/**
 * `GET /dashboard/frameworks` row (`ComplianceFramework` + `ComplianceRule[]`
 * in `IComplianceService.cs`). No current caller reads this — kept loose on
 * `rules` (verified only the framework-level fields) rather than over-fitting
 * a shape nothing consumes.
 */
export interface ComplianceFrameworkSummary {
  id: string
  name: string
  version?: string | null
  description?: string | null
  isActive: boolean
  createdAt: string
  rules: unknown[]
}

// ── Executive Report normalization helpers ──
// The Container API may return PascalCase or camelCase keys depending on
// serializer settings; these helpers accept either form defensively.
//
// `raw` is genuinely untyped input (a plain HTTP JSON payload) — typed as
// `Record<string, unknown>` rather than `any` so every read below still has
// to go through an explicit cast, preserving the original any-typed
// behavior (silent undefined on a missing/mistyped field) without letting
// `any` leak into this function's contract.

/**
 * Normalize one `ComplianceDashboard.CriticalDrift` row to the camelCase
 * `ConfigurationDrift` shape the UI consumes.
 *
 * The `/dashboard/tenant/{id}` endpoint serializes with PascalCase keys
 * (hence the `OverallScore || overallScore` reads in `getDashboardStats`),
 * unlike `/configurationdrift` which returns camelCase via the ApiResponse
 * envelope. The CriticalDrift list is a server-side `List<ConfigurationDrift>`:
 * on Hybrid tenants it already UNIONs DSC + TCM critical/high rows
 * (`ComplianceService.GetCriticalDriftAsync`), with TCM rows projected to the
 * ConfigurationDrift shape and tagged `SourceCollector = 'TCM'`. Only the
 * fields that projection populates are read; everything else defaults to a
 * safe empty so the Top-items row's optional-chaining stays null-safe. Reads
 * both casings so it survives if the endpoint is ever flipped to camelCase.
 */
function normalizeDashboardCriticalDrift(raw: Record<string, unknown>): ConfigurationDrift {
  const get = (pascal: string, camel: string): unknown => raw?.[pascal] ?? raw?.[camel]
  const severityRaw = get('Severity', 'severity')
  const statusRaw = get('Status', 'status')
  const riskRaw = get('RiskScore', 'riskScore')
  return {
    id: (get('Id', 'id') as string | undefined) ?? '',
    tenantId: (get('TenantId', 'tenantId') as string | undefined) ?? '',
    component: (get('Component', 'component') as string | undefined) ?? '',
    componentName: (get('ComponentName', 'componentName') as string | undefined) ?? undefined,
    setting: (get('Setting', 'setting') as string | undefined) ?? '',
    oldValue: (get('OldValue', 'oldValue') as string | undefined) ?? '',
    newValue: (get('NewValue', 'newValue') as string | undefined) ?? '',
    // The dashboard serializer emits the DriftSeverity enum as its name
    // string ("Critical"/"High"/…), which matches the FE string enum; fall
    // back to 'Low' only if a row arrives without one.
    severity: (typeof severityRaw === 'string' && severityRaw.length > 0
      ? severityRaw
      : 'Low') as ConfigurationDrift['severity'],
    status: (typeof statusRaw === 'string' && statusRaw.length > 0
      ? statusRaw
      : 'New') as ConfigurationDrift['status'],
    detectedAt: (get('DetectedAt', 'detectedAt') as string | undefined) ?? '',
    humanReadableDescription:
      (get('HumanReadableDescription', 'humanReadableDescription') as string | undefined) ?? undefined,
    riskScore: typeof riskRaw === 'number' ? riskRaw : undefined,
    // TCM projection does not carry framework impact — default to [] so the
    // Top-items row's `?.length ?? 0` read is correct rather than undefined.
    complianceFrameworkImpact:
      (get('ComplianceFrameworkImpact', 'complianceFrameworkImpact') as ComplianceFrameworkImpact[] | undefined) ?? [],
    sourceCollector: (get('SourceCollector', 'sourceCollector') as string | undefined) ?? undefined,
  }
}

// Helper to normalize metric trend result from C# PascalCase. Not part of
// the public `api` surface (nothing outside this module called
// `api.normalizeMetricTrendResult` before the split), so it stays a private
// function rather than a re-exported member.
function normalizeMetricTrendResult(data: Record<string, unknown> | undefined): MetricTrendResult {
  if (!data) {
    return {
      metricType: '',
      currentValue: 0,
      previousWeekValue: null,
      changeAmount: null,
      changePercentage: null,
      direction: 'flat',
      history: []
    }
  }
  return {
    metricType: (data.MetricType as string) || (data.metricType as string) || '',
    currentValue: (data.CurrentValue as number) || (data.currentValue as number) || 0,
    previousWeekValue: (data.PreviousWeekValue as number | null) ?? (data.previousWeekValue as number | null) ?? null,
    changeAmount: (data.ChangeAmount as number | null) ?? (data.changeAmount as number | null) ?? null,
    changePercentage: (data.ChangePercentage as number | null) ?? (data.changePercentage as number | null) ?? null,
    direction: ((data.Direction as string) || (data.direction as string) || 'flat') as 'up' | 'down' | 'flat',
    history: (((data.History ?? data.history) as Array<Record<string, unknown>> | undefined) ?? []).map((h) => ({
      date: (h.Date as string) ?? (h.date as string),
      value: (h.Value as number) ?? (h.value as number),
      context: (h.Context as string | undefined) ?? (h.context as string | undefined)
    }))
  }
}

// Dashboard APIs
export async function getDashboardStats(tenantId: string): Promise<DashboardStats> {
  try {
    const response = await apiClient.get(`/dashboard/tenant/${tenantId}`)
    const complianceDashboard = response.data

    if (import.meta.env.DEV) {
      console.log('API Response:', complianceDashboard)
    }

    // Handle C# PascalCase property names
    const overallScore = complianceDashboard.OverallScore || complianceDashboard.overallScore
    const metrics = complianceDashboard.Metrics || complianceDashboard.metrics
    const lastUpdated = complianceDashboard.LastUpdated || complianceDashboard.lastUpdated

    // Drift trend series: prefer the server-computed DSC + TCM union
    // (ComplianceDashboard.DriftTrends) so TCM-owned drift on Hybrid tenants is
    // counted in the "opened this month" verdict + Command Center sparkline.
    // Fall back to the legacy DSC-only client derivation for older payloads /
    // DSC-only tenants (mapDriftTrendSeries returns null when the field is absent).
    const driftTrends =
      mapDriftTrendSeries(complianceDashboard.DriftTrends ?? complianceDashboard.driftTrends)
      ?? (await getDriftTrends(tenantId))

    // Transform C# ComplianceDashboard to React DashboardStats
    return {
      totalTenants: 1, // Single tenant for now
      ...readActiveDrifts(metrics),
      pendingProcessing: metrics?.PendingProcessing || metrics?.pendingProcessing || 0,
      criticalIssues: overallScore?.IssuesBySeverity?.Critical || overallScore?.issuesBySeverity?.Critical || 0,
      issuesBySeverity: {
        critical: overallScore?.IssuesBySeverity?.Critical || overallScore?.issuesBySeverity?.Critical || 0,
        high: overallScore?.IssuesBySeverity?.High || overallScore?.issuesBySeverity?.High || 0,
        medium: overallScore?.IssuesBySeverity?.Medium || overallScore?.issuesBySeverity?.Medium || 0,
        low: overallScore?.IssuesBySeverity?.Low || overallScore?.issuesBySeverity?.Low || 0,
      },
      complianceScore: (() => {
        const totalChecks = overallScore?.TotalChecks || overallScore?.totalChecks || 0
        const passedChecks = overallScore?.PassedChecks || overallScore?.passedChecks || 0
        const failedChecks = overallScore?.FailedChecks || overallScore?.failedChecks || 0

        // Calculate correct percentage: passed / total * 100
        // Use Math.floor to be conservative - any failed checks should prevent 100%
        const calculatedPercentage = totalChecks > 0 ? Math.floor((passedChecks / totalChecks) * 100) : 0

        // Determine level based on calculated percentage
        let level: string
        if (calculatedPercentage >= 95) level = 'Excellent'
        else if (calculatedPercentage >= 85) level = 'Good'
        else if (calculatedPercentage >= 75) level = 'Fair'
        else if (calculatedPercentage >= 60) level = 'Poor'
        else level = 'Critical'

        return {
          percentage: calculatedPercentage,
          level,
          totalChecks,
          passedChecks,
          failedChecks,
          lastUpdated: lastUpdated || new Date().toISOString()
        }
      })(),
      recentActivity: await getRecentActivity(tenantId),
      driftTrends,
      topRisks: [], // TODO: Map from critical drift
      // Server-side critical/high drift detail. On Hybrid tenants this is
      // the DSC + TCM union (GetCriticalDriftAsync); the Baseline Compliance
      // "Top items worth your eye" section reads it so TCM-owned drift
      // appears there, consistent with issuesBySeverity above.
      criticalDrift: ((complianceDashboard.CriticalDrift || complianceDashboard.criticalDrift || []) as Array<Record<string, unknown>>)
        .map(normalizeDashboardCriticalDrift),
    } as DashboardStats
  } catch (error) {
    console.error('Dashboard API Error:', error)
    // Return fallback data
    return {
      totalTenants: 1,
      activeDrifts: 0,
      activeDriftsKnown: false,
      pendingProcessing: 0,
      criticalIssues: 0,
      issuesBySeverity: { critical: 0, high: 0, medium: 0, low: 0 },
      complianceScore: {
        percentage: 0,
        level: 'Critical',
        totalChecks: 0,
        passedChecks: 0,
        failedChecks: 0,
        lastUpdated: new Date().toISOString()
      },
      recentActivity: [],
      driftTrends: [],
      topRisks: [],
      criticalDrift: []
    } as DashboardStats
  }
}

// Dashboard Trends API (week-over-week indicators)
export async function getDashboardTrends(tenantId: string): Promise<DashboardTrends | null> {
  try {
    const response = await apiClient.get(`/metrictrends/tenant/${tenantId}`)
    const data = response.data

    // Handle C# PascalCase property names
    return {
      secureScore: normalizeMetricTrendResult(data.SecureScore || data.secureScore),
      mfaAdoption: normalizeMetricTrendResult(data.MfaAdoption || data.mfaAdoption),
      baselineCompliance: normalizeMetricTrendResult(data.BaselineCompliance || data.baselineCompliance),
      generatedAt: data.GeneratedAt || data.generatedAt || new Date().toISOString()
    }
  } catch (error: unknown) {
    // 404 is expected when no snapshots exist yet
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.log('No trend data available yet for tenant', tenantId)
      return null
    }
    console.error('Dashboard trends API error:', error)
    return null
  }
}

// Baseline APIs
//
// `GetBaselineConfigurationAsync` returns `Dictionary<string, object>` on the
// Container side (`DashboardController.GetBaseline`) — a genuinely dynamic
// M365DSC baseline dump with no fixed shape, so `unknown` (not `any`) is the
// honest type here. No current caller reads this endpoint's payload.
export async function getBaseline(tenantId: string): Promise<Record<string, unknown>> {
  const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(`/dashboard/tenant/${tenantId}/baseline`)
  return response.data.data
}

export async function hasBaseline(tenantId: string): Promise<boolean> {
  const response = await apiClient.get<ApiResponse<boolean>>(`/dashboard/tenant/${tenantId}/baseline/exists`)
  return response.data.data
}

export async function getComplianceFrameworks(): Promise<ComplianceFrameworkSummary[]> {
  const response = await apiClient.get<ApiResponse<ComplianceFrameworkSummary[]>>('/dashboard/frameworks')
  return response.data.data
}

// Helper methods for dashboard data
export async function getRecentActivity(tenantId: string): Promise<ActivityFeedItem[]> {
  if (import.meta.env.DEV) {
    console.log(`[${new Date().toISOString()}] Starting getRecentActivity for tenant: ${tenantId}`)
  }

  try {
    // Call the new backend activity endpoint which aggregates:
    // - Recent scan jobs
    // - Active drift detections
    // - Approval/rejection events with user accountability ("who did what")
    const response = await apiClient.get<ActivityFeedItem[] | ApiResponse<ActivityFeedItem[]>>(`/activity/tenant/${tenantId}?limit=15`)

    // Backend may return either a bare array or an envelope `{ data: [...] }`.
    const activities = Array.isArray(response.data) ? response.data : ((response.data as ApiResponse<ActivityFeedItem[]>)?.data || [])

    if (import.meta.env.DEV) {
      console.log(`getRecentActivity complete for tenant ${tenantId}: ${activities.length} activities`)
    }
    return activities
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`getRecentActivity failed for tenant ${tenantId}, falling back to empty array:`, error)
    }

    // Fallback: return empty array if backend call fails
    // The UI will show "No recent activity" which is better than breaking
    return []
  }
}

export async function getDriftTrends(tenantId: string): Promise<DriftTrendPoint[]> {
  if (import.meta.env.DEV) {
    console.log(`[${new Date().toISOString()}] getDriftTrends called for tenant: ${tenantId}`)
  }

  try {
    // Get drift data for the last 30 days
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
    if (import.meta.env.DEV) {
      console.log(`Fetching drifts from ${thirtyDaysAgo} to now`)
    }

    const driftResponse = await getConfigurationDrifts({
      tenantId,
      fromDate: thirtyDaysAgo,
      page: 1,
      limit: 1000 // Get all drifts in period
    })

    if (import.meta.env.DEV) {
      console.log(`API Response received:`, {
        itemCount: driftResponse.items?.length || 0,
        total: driftResponse.total,
        hasItems: !!driftResponse.items,
        firstItem: driftResponse.items?.[0] ? {
          detectedAt: driftResponse.items[0].detectedAt,
          severity: driftResponse.items[0].severity,
          component: driftResponse.items[0].component,
          status: driftResponse.items[0].status
        } : null,
        queryParams: {
          tenantId,
          fromDate: thirtyDaysAgo,
          page: 1,
          limit: 1000
        }
      })
    }

    // Continue even if no items - will show flat line at zero (compliant tenant)
    if (!driftResponse.items || driftResponse.items.length === 0) {
      if (import.meta.env.DEV) {
        console.warn(`No drift items found for tenant ${tenantId} in last 30 days`)
        console.warn(`Query params: tenantId=${tenantId}, fromDate=${thirtyDaysAgo}`)
        console.warn(`This could mean: 1) Tenant is 100% compliant, 2) Drifts are older than 30 days, or 3) Drifts have been cleaned up`)
        console.warn(`Continuing to show 30-day trend with zero counts`)
      }
      // Don't return early - continue to show zero-count trend line
    }

    // Group drifts by day and severity
    const dailyTrends = new Map<string, { critical: number, high: number, medium: number, low: number, info: number }>()

    // Initialize last 30 days with zero counts (using UTC to match database timestamps)
    for (let i = 0; i < 30; i++) {
      const date = new Date()
      date.setUTCHours(0, 0, 0, 0)  // Set to UTC midnight to ensure consistent date boundaries
      date.setUTCDate(date.getUTCDate() - i)  // Subtract days in UTC timezone
      const dateKey = date.toISOString().split('T')[0]
      dailyTrends.set(dateKey, { critical: 0, high: 0, medium: 0, low: 0, info: 0 })
    }

    if (import.meta.env.DEV) {
      console.log(`Initialized ${dailyTrends.size} days with zero counts`)
    }

    // Count drifts by day and severity
    let processedCount = 0
    let skippedCount = 0

    driftResponse.items.forEach(drift => {
      const driftDate = new Date(drift.detectedAt).toISOString().split('T')[0]
      const existing = dailyTrends.get(driftDate)

      if (existing) {
        const severity = drift.severity.toLowerCase()
        if (severity === 'critical') existing.critical++
        else if (severity === 'high') existing.high++
        else if (severity === 'medium') existing.medium++
        else if (severity === 'low') existing.low++
        else existing.info++
        processedCount++
      } else {
        skippedCount++
        if (import.meta.env.DEV && skippedCount <= 3) { // Log first 3 skipped items
          console.warn(`Drift skipped (date outside 30-day window): ${driftDate}`, drift)
        }
      }
    })

    if (import.meta.env.DEV) {
      console.log(`Processed ${processedCount} drifts, skipped ${skippedCount} (outside date range)`)
    }

    // Calculate total drifts across all severity levels
    let totalDrifts = 0
    dailyTrends.forEach(counts => {
      totalDrifts += counts.critical + counts.high + counts.medium + counts.low + counts.info
    })

    if (import.meta.env.DEV) {
      console.log(`Total drifts across all days: ${totalDrifts}`)
    }

    // Convert to array format for chart, aggregating every 4 days for readability
    // IMPORTANT: Aggregate counts instead of sampling to avoid losing drift data
    const trends: DriftTrendPoint[] = []
    const sortedDates = Array.from(dailyTrends.keys()).sort()

    // Aggregate every 4 days starting from oldest
    for (let i = 0; i < sortedDates.length; i += 4) {
      // Aggregate counts for this 4-day interval
      const intervalEnd = Math.min(i + 4, sortedDates.length)
      const aggregatedCounts = {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
        info: 0
      }

      // Sum up counts for all days in this interval
      for (let j = i; j < intervalEnd; j++) {
        const counts = dailyTrends.get(sortedDates[j])
        if (!counts) continue
        aggregatedCounts.critical += counts.critical
        aggregatedCounts.high += counts.high
        aggregatedCounts.medium += counts.medium
        aggregatedCounts.low += counts.low
        aggregatedCounts.info += counts.info
      }

      // Use the first date of the interval as the label
      const intervalDate = sortedDates[i]
      trends.push({
        date: new Date(intervalDate).toISOString(),
        ...aggregatedCounts
      })
    }

    // Always include the most recent day separately if it's not already the last interval
    const mostRecentDate = sortedDates[sortedDates.length - 1]
    const mostRecentIsoDate = new Date(mostRecentDate).toISOString()
    const lastTrendDate = trends.length > 0 ? trends[trends.length - 1].date : null

    if (!lastTrendDate || lastTrendDate !== mostRecentIsoDate) {
      const counts = dailyTrends.get(mostRecentDate)
      if (counts) {
        trends.push({
          date: mostRecentIsoDate,
          ...counts
        })
      }
    }

    const finalTrends = trends.reverse() // Chronological order

    if (import.meta.env.DEV) {
      console.log(`Final trends array:`, {
        length: finalTrends.length,
        dateRange: finalTrends.length > 0 ? {
          start: finalTrends[0].date,
          end: finalTrends[finalTrends.length - 1].date
        } : null,
        samplePoint: finalTrends[0]
      })

      // Log each data point for debugging
      console.log(`COMPLETE DRIFT TRENDS DATA (${finalTrends.length} points):`)
      finalTrends.forEach((point, index) => {
        const total = point.critical + point.high + point.medium + point.low + point.info
        console.log(`  [${index}] ${point.date} -> Total: ${total} (Critical: ${point.critical}, High: ${point.high}, Medium: ${point.medium}, Low: ${point.low}, Info: ${point.info})`)
      })
    }

    return finalTrends

  } catch (error) {
    if (import.meta.env.DEV) {
      console.error(`Error fetching drift trends for tenant ${tenantId}:`, error)
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        })
      }
      // Return empty array on error - no fake data
      console.warn('Returning empty array due to error - chart will show "No trend data available"')
    }
    return []
  }
}

export const dashboardApi = {
  getDashboardStats,
  getDashboardTrends,
  getBaseline,
  hasBaseline,
  getComplianceFrameworks,
  getRecentActivity,
  getDriftTrends,
}
